import React from "react";
import { Avatar, Button, Tooltip } from "antd";
import { EnvironmentOutlined, LoginOutlined, LogoutOutlined } from "@ant-design/icons";
import { Localize } from "components/service";
import { timeFormat } from "utils/helper-functions";
import { getInitials, getPlatformMeta, getScanReasonMeta, getScanResultMeta, hasScanLocation, PlaceholderTableLink } from "./event-attendance-helper";

const getScanLocation = (log) => ({
    ...log,
    lat: log.latitude,
    lng: log.longitude,
});

const checkInActionMeta = {
    tone: "check-in",
    icon: <LoginOutlined />,
};

const checkOutActionMeta = {
    tone: "check-out",
    icon: <LogoutOutlined />,
};

const SoftTag = ({ children, icon, meta }) => (
    <span className={ `event-check-soft-tag event-check-soft-tag--${ meta.tone }` }>
        { icon }
        { children }
    </span>
);

const ActionText = ({ children, meta }) => (
    <span className={ `event-check-action-text event-check-action-text--${ meta.tone }` }>
        { meta.icon }
        <span>{ children }</span>
    </span>
);

const ReasonText = ({ children, meta }) => (
    <span className={ `event-check-reason-text event-check-reason-text--${ meta.tone }` }>
        { meta.icon }
        <span>{ children }</span>
    </span>
);

const EventCheckInLogsTableHelper = (openLocation) => {
    const columns = [
        {
            title: <Localize>TABLES.Column_Title_Worker</Localize>,
            dataIndex: "worker",
            width: "16%",
        },
        {
            title: <Localize>TABLES.Column_Title_Subcontractor</Localize>,
            dataIndex: "subcontractor",
            width: "15%",
        },
        {
            title: <Localize>TABLES.Column_Title_ActionType</Localize>,
            dataIndex: "action",
            width: "10%",
        },
        {
            title: <Localize>TABLES.Column_Title_Result</Localize>,
            dataIndex: "result",
            width: "10%",
        },
        {
            title: <Localize>TABLES.Column_Title_Time</Localize>,
            dataIndex: "time",
            width: "13%",
        },
        {
            title: <Localize>TABLES.Column_Title_Reason</Localize>,
            dataIndex: "reason",
            width: "20%",
        },
        {
            title: (
                <Tooltip title={ <Localize>TABLES.Tooltip_AttendanceDevice</Localize> }>
                    <span><Localize>TABLES.Column_Title_Device</Localize></span>
                </Tooltip>
            ),
            dataIndex: "device",
            width: "11%",
        },
        {
            title: (
                <Tooltip title={ <Localize>TABLES.Tooltip_ScanLocation</Localize> }>
                    <span><Localize>TABLES.Column_Title_ScanLocation</Localize></span>
                </Tooltip>
            ),
            dataIndex: "location",
            align: "center",
            width: "5%",
        },
    ];

    const data = (logs) => {
        if (!logs)
        {
            return [];
        }

        return logs.map((log) => {
            const workerName = `${ log.worker.name } ${ log.worker.surname }`;
            const isCheckIn = log.action === "check_in";
            const reason = getScanReasonMeta(log.reason);
            const result = getScanResultMeta(log.result, log.reason);
            const action = isCheckIn ? checkInActionMeta : checkOutActionMeta;
            const platform = getPlatformMeta(log.platform);
            const canOpenLocation = hasScanLocation(log);

            return {
                key: log.id,
                worker: (
                    <span className="event-check-logs-worker">
                        <Avatar size={ 32 }>{ getInitials(log.worker) }</Avatar>
                        <PlaceholderTableLink>{ workerName }</PlaceholderTableLink>
                    </span>
                ),
                subcontractor: <PlaceholderTableLink>{ log.subcontractor.title }</PlaceholderTableLink>,
                action: (
                    <ActionText meta={ action }>
                        { isCheckIn ?
                            <Localize>CHECK_LOGS.Tag_CheckIn</Localize> :
                            <Localize>CHECK_LOGS.Tag_CheckOut</Localize>
                        }
                    </ActionText>
                ),
                result: (
                    <SoftTag
                        icon={ result.icon }
                        meta={ result }>
                        <Localize>{ result.label }</Localize>
                    </SoftTag>
                ),
                time: timeFormat(log.created_at, "DD.MM.YYYY HH:mm:ss"),
                reason: (
                    <ReasonText meta={ reason }>
                        { reason.label ? <Localize>{ reason.label }</Localize> : log.reason }
                    </ReasonText>
                ),
                device: (
                    <Tooltip title={ log.user_agent || log.ip || log.device_uuid }>
                        <span className="event-check-logs-device">
                            { platform.icon }
                            <span className="event-check-logs-device__text">
                                <span>{ platform.label }</span>
                                { log.device_uuid && <small>{ log.device_uuid }</small> }
                            </span>
                        </span>
                    </Tooltip>
                ),
                location: (
                    <Tooltip title={
                        canOpenLocation ?
                            <Localize>TABLES.Tooltip_ScanLocation</Localize> :
                            <Localize>CHECK_LOGS.Tooltip_NoScanLocation</Localize>
                    }>
                        <span>
                            <Button
                                type="text"
                                disabled={ !canOpenLocation }
                                icon={ <EnvironmentOutlined /> }
                                onClick={ () => openLocation(getScanLocation(log)) } />
                        </span>
                    </Tooltip>
                ),
            };
        });
    };

    return {
        columns,
        data,
    };
};

export default EventCheckInLogsTableHelper;
